import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { sendContactForm } from "../lib/emailjs";
import { trackEvent } from "../lib/analytics";

export const intentLabels = {
  demo: "General demo",
  server: "Bancony Server",
  assistant: "Bancony Assistant",
  "compliance-pack": "Compliance pack request",
  "ai-act-2026": "EU AI Act readiness (spec sheet)",
  "ai-act-readiness": "EU AI Act readiness call",
  other: "Something else",
};

const intentKeys = Object.keys(intentLabels);

const roles = [
  "CIO",
  "Head of Digital",
  "CISO",
  "DPO",
  "Procurement",
  "Other",
];

const initial = (intent) => ({
  name: "",
  email: "",
  organization: "",
  role: "",
  country: "",
  intent: intentKeys.includes(intent) ? intent : "demo",
  message: "",
});

export const ContactForm = () => {
  const [params] = useSearchParams();
  const intentParam = params.get("intent");
  const [formData, setFormData] = useState(initial(intentParam));
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    sendContactForm(e.target)
      .then(() => {
        trackEvent("generate_lead", {
          form: "contact",
          lead_intent: formData.intent,
          lead_role: formData.role,
        });
        setSubmitted(true);
        setFormData(initial(intentParam));
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Contact form failed", err);
        trackEvent("form_error", { form: "contact", lead_intent: formData.intent });
        setError(
          "Something went wrong sending the message. Please email hello@bancony.com directly while we look into it."
        );
      })
      .finally(() => setSubmitting(false));
  };

  if (submitted) {
    return (
      <div className="contact-success">
        <div className="contact-success-icon">✓</div>
        <h3>Message sent.</h3>
        <p>Thank you. We'll be in touch shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} autoComplete="on" noValidate>
      <div className="field-row">
        <div className="field">
          <label htmlFor="contact-name">Full name</label>
          <input
            id="contact-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={onChange}
            required
            disabled={submitting}
            autoComplete="name"
          />
        </div>
        <div className="field">
          <label htmlFor="contact-email">Work email</label>
          <input
            id="contact-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={onChange}
            required
            disabled={submitting}
            autoComplete="email"
          />
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="contact-org">Company</label>
          <input
            id="contact-org"
            type="text"
            name="organization"
            value={formData.organization}
            onChange={onChange}
            required
            disabled={submitting}
            autoComplete="organization"
          />
        </div>
        <div className="field">
          <label htmlFor="contact-role">Role</label>
          <select
            id="contact-role"
            name="role"
            value={formData.role}
            onChange={onChange}
            required
            disabled={submitting}
          >
            <option value="" disabled>Select your role…</option>
            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="contact-country">Country</label>
          <input
            id="contact-country"
            type="text"
            name="country"
            value={formData.country}
            onChange={onChange}
            placeholder="Iceland, Germany, …"
            disabled={submitting}
            autoComplete="country-name"
          />
        </div>
        <div className="field">
          <label htmlFor="contact-intent">I'm interested in</label>
          <select
            id="contact-intent"
            name="intent"
            value={formData.intent}
            onChange={onChange}
            disabled={submitting}
          >
            {intentKeys.map((k) => (
              <option key={k} value={k}>{intentLabels[k]}</option>
            ))}
          </select>
          <input
            type="hidden"
            name="intent_label"
            value={intentLabels[formData.intent] || formData.intent}
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="contact-message">Tell us a bit more</label>
        <textarea
          id="contact-message"
          name="message"
          rows="5"
          value={formData.message}
          onChange={onChange}
          placeholder="Use case, timeline, anything we should know."
          disabled={submitting}
        />
      </div>

      {error ? (
        <p style={{ color: "#c43", marginBottom: 16, fontSize: 14 }}>{error}</p>
      ) : null}

      <button type="submit" className="btn btn-primary" disabled={submitting}>
        {submitting ? "Sending…" : "Send message"}
      </button>
    </form>
  );
};
